<template>
  <v-card
    tile
    class="full-height"
    style="background-color: black;"
  >
    <v-btn
      absolute
      top
      left
      icon
      dark
      x-large
      style="z-index: 1;"
      @click="close"
    >
      <v-icon>
        close
      </v-icon>
    </v-btn>

    <v-btn
      absolute
      top
      right
      dark
      x-large
      text
      style="z-index: 1;"
      @click="showGenderSelection = true"
    >
      Lähetä

      <v-icon
        class="ml-4"
      >
        send
      </v-icon>
    </v-btn>

    <v-layout
      v-if="loading"
      justify-center
      align-center
      fill-height
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </v-layout>

    <canvas
      ref="canvas"
      :style="{
        opacity: loading ? 0 : 1,
      }"
    />

    <div
      :style="{
        position: 'absolute',
        left: 0,
        bottom: 0,
      }"
      class="full-width px-6 pb-6 d-flex align-center"
    >
      <v-btn
        :disabled="!canUndo"
        color="white"
        icon
        :style="{
          borderTop: '4px solid white',
          opacity: canUndo ? 1 : 0.5,
        }"
        @click="undo"
      >
        <div
          class="d-flex flex-column align-center"
        >
          <v-icon
            :style="{
              color: 'white !important',
            }"
          >
            undo
          </v-icon>

          <span
            class="caption-xxs mt-1 mb-n4 white--text"
          >
            undo
          </span>
        </div>
      </v-btn>

      <v-btn
        :disabled="!canRedo"
        color="white"
        icon
        :style="{
          borderTop: '4px solid white',
          opacity: canRedo ? 1 : 0.5,
        }"
        class="ml-4"
        @click="redo"
      >
        <div
          class="d-flex flex-column align-center"
        >
          <v-icon
            :style="{
              color: 'white !important',
            }"
          >
            redo
          </v-icon>

          <span
            class="caption-xxs mt-1 mb-n4 white--text"
          >
            redo
          </span>
        </div>
      </v-btn>

      <v-spacer />

      <v-btn
        color="white"
        icon
        :style="{
          borderTop: '4px solid white',
        }"
        @click="rotate"
      >
        <div
          class="d-flex flex-column align-center"
        >
          <v-icon
            :style="{
              color: 'white !important',
            }"
          >
            rotate_90_degrees_ccw
          </v-icon>

          <span
            class="caption-xxs mt-1 mb-n4 white--text"
          >
            rotate
          </span>
        </div>
      </v-btn>

      <v-spacer />

      <v-menu
        offset-y
        top
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            icon
            color="white"
            style="border-top: 4px solid white;"
            class="mr-4"
          >
            <div
              class="d-flex flex-column align-center"
            >
              <div
                :style="{
                  backgroundColor: 'white',
                  width: `${selectedThickness}px`,
                  height: '24px',
                  transform: 'rotate(45deg)',
                  borderRadius: '20px',
                }"
              />

              <span
                class="caption-xxs mt-1 mb-n4 white--text"
              >
                width
              </span>
            </div>
          </v-btn>
        </template>
        <v-list
          dense
        >
          <v-list-item
            v-for="thickness in [2, 6, 12, 20]"
            :key="thickness"
            class="pl-3"
            @click="selectedThickness = thickness"
          >
            <v-list-item-avatar
              class="mr-3 my-0"
            >
              <div
                :style="{
                  backgroundColor: 'black',
                  width: `${thickness}px`,
                  height: '48px',
                  transform: 'rotate(45deg)',
                  borderRadius: '20px',
                }"
              />
            </v-list-item-avatar>
            <v-list-item-title>
              {{ thickness }}px
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-menu
        offset-y
        top
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            :color="selectedColor.hex || 'white'"
            icon
            style="border-top: 4px solid white;"
          >
            <div
              class="d-flex flex-column align-center"
            >
              <v-icon>
                color_lens
              </v-icon>

              <span
                class="caption-xxs mt-1 mb-n4 white--text"
              >
                color
              </span>
            </div>
          </v-btn>
        </template>
        <v-list
          dense
        >
          <v-list-item
            v-for="(color, i) in colors"
            :key="i"
            class="pl-3"
            @click="selectedColor = color"
          >
            <v-list-item-avatar
              class="mr-3 my-0"
            >
              <div
                :style="{
                  backgroundColor: color.hex,
                  width: '24px',
                  height: '24px',
                  borderRadius: '100%',
                }"
              />
            </v-list-item-avatar>
            <v-list-item-title>
              {{ color.label }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-dialog
      v-model="showGenderSelection"
    >
      <gender-selection
        :on-submit="submit"
        :current-action="currentAction"
        :on-close="() => showGenderSelection = false"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { fabric } from 'fabric';
import { DirectUpload } from '@rails/activestorage';
import { contain } from '@/mixins/scaleToCanvas';
import { getAccessToken } from '@/store/utils';

export default {
  mixins: [
    contain,
  ],

  components: {
    GenderSelection: () => import('./GenderSelection.vue'),
  },

  data: () => ({
    url: `${process.env.VUE_APP_API_URL}/api/v2/direct_uploads`,
    showGenderSelection: false,
    drawer: null,
    currentCanvasImg: null,
    currentAngle: 0,
    loading: false,
    fabric: null,
    isRedoing: false,
    history: [],
    selectedColor: {
      label: 'Punainen',
      hex: '#F44336',
    },
    selectedThickness: 4,
    colors: [
      {
        label: 'Keltainen',
        hex: '#FFEB3B',
      },
      {
        label: 'Musta',
        hex: '#000000',
      },
      {
        label: 'Punainen',
        hex: '#F44336',
      },
      {
        label: 'Sininen',
        hex: '#2196F3',
      },
      {
        label: 'Valkoinen',
        hex: '#FFFFFF',
      },
    ],
  }),

  watch: {
    selectedColor(newVal) {
      this.drawer.freeDrawingBrush.color = newVal.hex;
    },

    selectedThickness(newVal) {
      this.drawer.freeDrawingBrush.width = newVal;
    },
  },

  computed: {
    ...mapGetters({
      dialogProps: 'dialog/dialogProps',
    }),

    imageUploaded() {
      return this.dialogProps.imageUploaded;
    },

    file() {
      return this.dialogProps.file;
    },

    finally() {
      return this.dialogProps.finally;
    },

    canUndo() {
      if (!this.drawer) return false;

      // We are comparing to length 1 since the first _object is the image.
      return this.drawer._objects.length > 1; // eslint-disable-line
    },

    canRedo() {
      if (!this.drawer) return false;

      return this.history.length > 0;
    },

    currentAction() {
      return this.dialogProps.currentAction;
    },
  },

  mounted() {
    this.initialize();
  },

  methods: {
    ...mapActions({
      closeDialog: 'dialog/closeDialog',
      locateUser: 'currentLocation/locateUser',
    }),

    async initialize() {
      this.loading = true;

      this.initDrawer();
      this.drawImage();

      this.loading = false;
    },

    /**
     * Uses always the original image but rotates
     * and draws it on canvas accordingly.
     */
    drawImage() {
      const image = new Image();

      image.onload = () => {
        let imageWidth;
        let imageHeight;

        if (this.currentAngle === 0 || this.currentAngle === 180) {
          ({
            width: imageWidth,
            height: imageHeight,
          } = image);
        } else {
          ({
            width: imageHeight,
            height: imageWidth,
          } = image);
        }

        const canvasWidth = this.drawer.getWidth();
        const canvasHeight = this.drawer.getHeight();

        const params = {
          parentWidth: canvasWidth,
          parentHeight: canvasHeight,
          childWidth: imageWidth,
          childHeight: imageHeight,
        };

        const {
          width,
          height,
          offsetX,
          offsetY,
        } = this.contain(params);

        fabric.Image.fromURL(image.src, (img) => {
          const img1 = img.set({
            left: offsetX,
            top: offsetY,
            angle: this.currentAngle,
          });

          img1.scaleToWidth(width);
          img1.scaleToHeight(height);

          this.currentCanvasImg = img1;

          this.drawer.insertAt(img1, 0);
          this.drawer.centerObject(img1);
        });
      };

      const reader = new FileReader();

      reader.onload = ({ target }) => {
        image.src = target.result;
      };

      reader.readAsDataURL(this.file);
    },

    removeImage() {
      if (!this.currentCanvasImg) return;
      this.drawer.remove(this.currentCanvasImg);
    },

    initDrawer() {
      const { canvas } = this.$refs;
      const drawer = new fabric.Canvas(canvas);

      fabric.Object.prototype.transparentCorners = false;

      // drawer.historyInit();
      drawer.setWidth(window.innerWidth);
      drawer.setHeight(window.innerHeight);

      // Settings
      drawer.freeDrawingBrush.color = this.selectedColor.hex;
      drawer.freeDrawingBrush.width = this.selectedThickness;
      drawer.isDrawingMode = true;

      drawer.on('object:added', () => {
        if (!this.isRedoing) {
          this.history = [];
        }
        this.isRedoing = false;
      });

      this.drawer = drawer;
    },

    close() {
      this.finally();
    },

    undo() {
      if (this.canUndo) { // eslint-disable-line
        this.history.push(this.drawer._objects.pop()); // eslint-disable-line
        this.drawer.renderAll();
      }
    },

    redo() {
      if (this.canRedo) {
        this.isRedoing = true;

        this.drawer.add(this.history.pop());
      }
    },

    /**
     * 1) Sets the angle
     * 2) Semoves the image
     * 3) Rotates user-drawn items
     * 4) Re-paints the image
     */
    rotate() {
      if (this.currentAngle > 0 && this.currentAngle % 270 === 0) {
        this.currentAngle = 0;
      } else {
        this.currentAngle += 90;
      }

      this.removeImage();

      /**
       * Sets angle on user-drawn items
       */
      this.drawer.forEachObject((obj) => {
        obj.set({
          angle: this.currentAngle,
        });

        this.drawer.centerObject(obj);
      });

      this.drawImage();
    },

    /**
     * Expects payload with the following attributes:
     * - gender
     * - allowContactQueries
     * - allowImageComments
     *
     * @param payload
     *
     * @param payload.gender
     * @param payload.allowContactQueries
     * @param payload.allowImageComments
     *
     * @returns {Promise<unknown>}
     */
    async submit(payload) {
      if (!this.$currentLocation) {
        await this.locateUser();
      }

      if (!this.$currentLocation) {
        this.$notifyError({ msg: 'Kuvaa ei voitu lähettää, koska sijainti ei ole saatavilla' });
        this.close();
        return false;
      }

      return new Promise((resolve) => {
        this.drawer.getElement().toBlob(async (blob) => {
          const params = {
            blob,
            ...payload,
          };

          await this.uploadImage(params);

          resolve();
        }, 'image/jpeg', 0.75);
      });
    },

    async uploadImage({ blob, ...rest }) {
      const image = await this.uploadBlob(blob);

      if (!image) return;

      await this.imageUploaded({ image, ...rest });
      this.close();
    },

    async uploadBlob(blob) {
      const directUploader = new DirectUpload(blob, this.url, this);

      try {
        const image = await new Promise((resolve, reject) => {
          directUploader.create((error, returnedBlob) => {
            if (error) return reject();

            return resolve(returnedBlob.signed_id);
          });
        });

        return image;
      } catch (e) {
        this.$notifyError({ msg: 'Kuvan lähettäminen epäonnistui' });
        return null;
      }
    },

    directUploadWillCreateBlobWithXHR(xhr) {
      xhr.setRequestHeader('Authorization', `Token token=${getAccessToken()}`);

      xhr.upload.addEventListener('progress', this.directUploadDidProgress);
    },

    directUploadWillStoreFileWithXHR(xhr) {
      xhr.upload.addEventListener('progress', this.directUploadDidProgress);
    },

    // uploadDidProgress(e) {
    //   if (!e.lengthComputable) return;
    //
    //   const currentProgress = (e.loaded / e.total * 100);
    // },
  },
};
</script>
